import { FC, MouseEvent, ReactElement, SVGProps, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import ArrowRightIcon from '@/assets/svg/ui-elements/arrow-right.svg';
import ChevronDownIcon from '@/assets/svg/ui-elements/chevron-down.svg';
import DotsVerticalIcon from '@/assets/svg/ui-elements/dots-vertical.svg';

import { theme } from '../../theme';
import Badge from '../Badge';
import BottomSheet from '../BottomSheet';
import { IconButton } from '../Button';
import { ExpandRegion } from '../MobileTableCard';
import TextButton from '../TextButton';
import Typography from '../Typography';
import {
  BadgeRow,
  CardCaption,
  CardDivider,
  CardFooter,
  CardHeader,
  CardLeading,
  CardRoot,
  CardTitle,
  CardTitleButton,
  CardTitles,
  DetailsInner,
  Field,
  FieldRow,
  FieldRows,
  SheetAction,
  SheetActionDivider,
  SheetActionList,
  ToggleChevron,
} from './MobileListCard.styled';
import type { MobileListCardAction, MobileListCardField, MobileListCardProps } from './types';

const MAX_BADGES = 2;
const MAX_FIELDS_PER_ROW = 2;

const toRows = (fields: MobileListCardField[]): MobileListCardField[][] =>
  fields.reduce<MobileListCardField[][]>((rows, field) => {
    const last = rows.at(-1);
    const canShare = last && !field.fullRow && last.length < MAX_FIELDS_PER_ROW && !last[0].fullRow;
    if (canShare) last.push(field);
    else rows.push([field]);
    return rows;
  }, []);

const FieldsRow = ({ fields }: { fields: MobileListCardField[] }) => (
  <FieldRow>
    {fields.map((field) => (
      <Field key={field.label}>
        <Typography variant="body4" color={theme.colors.text.secondary}>
          {field.label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {field.value}
        </Typography>
      </Field>
    ))}
  </FieldRow>
);

// Only a tap on the card's own surface opens the details page: not one on its controls, and not
// one inside the actions sheet, whose events bubble here through the React portal.
const isCardSurfaceClick = (event: MouseEvent<HTMLElement>) => {
  const target = event.target as HTMLElement;
  return event.currentTarget.contains(target) && target.closest('button, a') === null;
};

const orderActions = (actions: MobileListCardAction[]) => {
  const safe = actions.filter((action) => action.tone !== 'destructive');
  const destructive = actions.filter((action) => action.tone === 'destructive');
  return { safe, destructive };
};

export const MobileListCard: FC<MobileListCardProps> = ({
  title,
  caption,
  leading,
  actions = [],
  actionsLabel,
  badges = [],
  summary,
  details = [],
  expanded: expandedProp,
  onExpandedChange,
  link,
  onOpen,
  highlight = false,
  className,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const expanded = expandedProp ?? internalExpanded;
  const hasDetails = details.length > 0;
  const shownBadges = badges.slice(0, MAX_BADGES);
  const hiddenBadgeCount = badges.length - shownBadges.length;
  const [singleAction] = actions;
  const isSingleIconAction = actions.length === 1 && Boolean(singleAction.icon);
  const { safe, destructive } = orderActions(actions);

  const setExpanded = (next: boolean) => {
    if (expandedProp === undefined) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  const handleCardClick = (event: MouseEvent<HTMLElement>) => {
    if (onOpen && isCardSurfaceClick(event)) onOpen();
  };

  const runSheetAction = (action: MobileListCardAction) => {
    setSheetOpen(false);
    action.onSelect();
  };

  const renderSheetAction = (action: MobileListCardAction) => (
    <SheetAction
      key={action.id}
      type="button"
      $destructive={action.tone === 'destructive'}
      disabled={action.disabled}
      onClick={() => runSheetAction(action)}
    >
      {action.icon && <span data-icon>{action.icon}</span>}
      <Typography variant="body2" as="span">
        {action.label}
      </Typography>
    </SheetAction>
  );

  return (
    <CardRoot
      className={className}
      $clickable={Boolean(onOpen)}
      data-highlight={highlight ? 'true' : undefined}
      onClick={handleCardClick}
    >
      <CardHeader>
        {leading && <CardLeading>{leading}</CardLeading>}
        <CardTitles>
          {onOpen ? (
            // The keyboard and screen-reader route to `onOpen`; the card surface is pointer-only.
            <CardTitleButton type="button" onClick={onOpen}>
              <CardTitle variant="body2" fontWeight={500} as="span">
                {title}
              </CardTitle>
            </CardTitleButton>
          ) : (
            <CardTitle variant="body2" fontWeight={500}>
              {title}
            </CardTitle>
          )}
          {caption && <CardCaption variant="body3">{caption}</CardCaption>}
        </CardTitles>

        {isSingleIconAction && (
          <IconButton
            variant="ghost"
            color="tertiary"
            size="sm"
            aria-label={singleAction.label}
            disabled={singleAction.disabled}
            onClick={singleAction.onSelect}
          >
            {singleAction.icon as ReactElement<SVGProps<SVGSVGElement>>}
          </IconButton>
        )}
        {actions.length > 0 && !isSingleIconAction && (
          <IconButton
            variant="ghost"
            color="tertiary"
            size="sm"
            aria-label={actionsLabel ?? `${title} actions`}
            onClick={() => setSheetOpen(true)}
          >
            <DotsVerticalIcon />
          </IconButton>
        )}
      </CardHeader>

      {badges.length > 0 && (
        <BadgeRow>
          {shownBadges.map((badge) => (
            <Badge key={badge.label} label={badge.label} color={badge.color ?? 'default'} />
          ))}
          {hiddenBadgeCount > 0 && <Badge label={`+${hiddenBadgeCount}`} color="default" />}
        </BadgeRow>
      )}

      <CardDivider />

      <FieldRows>
        <FieldsRow fields={summary.slice(0, MAX_FIELDS_PER_ROW)} />
      </FieldRows>

      {hasDetails && (
        <ExpandRegion $open={expanded} inert={!expanded}>
          <div>
            <DetailsInner>
              {toRows(details).map((row) => (
                <FieldsRow key={row.map((field) => field.label).join('|')} fields={row} />
              ))}
            </DetailsInner>
          </div>
        </ExpandRegion>
      )}

      {(hasDetails || link) && (
        <CardFooter>
          {hasDetails ? (
            <TextButton aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Hide details' : 'Show details'}
              <ToggleChevron $open={expanded} aria-hidden>
                <ChevronDownIcon />
              </ToggleChevron>
            </TextButton>
          ) : (
            <span />
          )}
          {link && (
            // Navigation, so it is a link: Base UI would otherwise announce a non-native button.
            <TextButton
              color="neutral"
              nativeButton={false}
              render={<RouterLink to={link.to} />}
              role="link"
            >
              {link.label}
              <ArrowRightIcon aria-hidden />
            </TextButton>
          )}
        </CardFooter>
      )}

      {actions.length > 0 && !isSingleIconAction && (
        <BottomSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={caption ? `${title} · ${caption}` : title}
          showClose
        >
          <SheetActionList>
            {safe.map(renderSheetAction)}
            {safe.length > 0 && destructive.length > 0 && <SheetActionDivider role="separator" />}
            {destructive.map(renderSheetAction)}
          </SheetActionList>
        </BottomSheet>
      )}
    </CardRoot>
  );
};

MobileListCard.displayName = 'MobileListCard';
